import { IContextualMenuItem, Spinner, SpinnerSize } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ContentManagerCopyMoveModeEnum } from "../../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { useLazyGetCopyJobProgressQuery, useLazyRenderListAsStreamQuery } from "../../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { DocubirdCache, sharepoint } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { resetContentManagerMoveTo, setContentManagerMoveTo, incrementContentManagerMoveToCompletedItemCount, setContentManagerMoveToCopyJobResults, setContentManagerMoveToCopyJobResultsIdsToRemove, setContentManagerMoveToError, setContentManagerMoveToSelectedFilesIsBusyProgress } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { GridProps } from "../useContentManagerBody";
import { createCopyJobs } from "../../../src/taskpane/services/SharePointRestServices";
import { CacheHelper } from "../../../utilities/CacheHelper";

export function useMenuItem_SelectedMoveFileCount(setIsCMMoveFilesListPanelOpen: (isCMMoveFilesListPanelOpen: boolean) => void, isInOneDrive: boolean) {
  const languageStrings = React.useContext(LanguageContext);

  const dispatch = useDispatch();

  const moveToEnabled = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.moveToEnabled);
  const moveToProgressCountTotal = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.progressCountTotal);
  const moveTobusyMovingFiles = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.busyMovingFiles);
  const moveTobusyFetchingFiles = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.busyFetchingFiles);
  const moveToIsMoveCancelled = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.isMoveCancelled);
  const moveToCopyJobResults = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.copyJobResults);
  const moveToMode = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.mode);
  const moveToIsMoveInProgress = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.isMoveInProgress);
  const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);
  const moveToSourceDirectory = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.workingDirectories.length > 0 ? state.contentManagerDirectory.moveTo.workingDirectories[state.contentManagerDirectory.moveTo.workingDirectories.length - 1] : null);
  const moveToError = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.error);

  const [menuItem_SelectedMoveFileCount, setMenuItem_SelectedMoveFileCount] = React.useState<IContextualMenuItem>();

  const [getCopyJobProgress] = useLazyGetCopyJobProgressQuery();
  const [getListAsStream] = useLazyRenderListAsStreamQuery();

  React.useEffect(() => {
    //Move/Copy All - Fetching all files to create CopyJob
    let nextHref = "";
    let totalCount = 0;
    let cancelled = moveToIsMoveCancelled;

    if (moveTobusyFetchingFiles && !cancelled) {
      dispatch(setContentManagerMoveTo({ moveToEnabled: false }));
      (async () => {
        const gridProps: GridProps = {
          sort: []
        }

        const sourceIsInOneDrive = moveToSourceDirectory?.type == ComponentTypes.OneDrive || moveToSourceDirectory?.type == ComponentTypes.OneDriveSubFolder;

        let filesToMoveCopy: any[] = [];
        do {
          const listStream = await getListAsStream({
            siteUrl: moveTo.sourceSiteUrl,
            sharePointListId: moveToSourceDirectory?.sharePointListId,
            viewXml: sharepoint.moveCopyAllViewXml,
            gridProps,
            accessToken: sourceIsInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
            serverRelativePath: moveTo.sourceServerRelPath,
            filterSections: [],
            nextPageUrl: nextHref
          }).unwrap();

          filesToMoveCopy = filesToMoveCopy.concat(listStream.ListData?.Row);

          totalCount = listStream.ListData?.Row ? totalCount + listStream.ListData?.Row.length : totalCount + 0;
          dispatch(setContentManagerMoveTo({ progressCountTotal: totalCount }));

          nextHref = listStream.ListData?.NextHref ? listStream.ListData?.NextHref : "";
        } while (nextHref.length > 0 && !cancelled);

        dispatch(setContentManagerMoveTo({ isPreparingJobs: true }));

        let copyJobResults = await createCopyJobs({
          siteUrl: moveTo.siteUrl,
          accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          files: filesToMoveCopy,
          sourceSiteUrl: moveTo.sourceSiteUrl,
          sourceServerRelPath: moveTo.sourceServerRelPath,
          destinationServerRelPath: moveTo.destinationServerRelPath,
          isMoveMode: (moveTo.mode === ContentManagerCopyMoveModeEnum.Move || moveTo.mode === ContentManagerCopyMoveModeEnum.MoveAll) ? true : false
        });

        dispatch(setContentManagerMoveTo({ busyFetchingFiles: false, busyMovingFiles: true, moveToEnabled: false, isPreparingJobs: false }));

        if (copyJobResults?.success) {
          dispatch(setContentManagerMoveToCopyJobResults(copyJobResults?.jobs))
        } else {
          dispatch(resetContentManagerMoveTo());
          dispatch(setContentManagerMoveToError(copyJobResults.error));
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [moveTobusyFetchingFiles, isInOneDrive, moveToSourceDirectory, moveToIsMoveCancelled]);

  const [fetchProgress, setFetchProgress] = React.useState(0);

  React.useEffect(() => {
    if (moveTobusyMovingFiles && moveToCopyJobResults?.length > 0) {
      setFetchProgress(prev => prev + 1);
    }
  }, [moveTobusyMovingFiles, moveToCopyJobResults]);

  React.useEffect(() => {
    let timer;
    if (fetchProgress > 0) {
      timer = setTimeout(() => {
        updateProgress();
      }, 1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [fetchProgress]);

  const updateProgress = async () => {
    const copyJobResultsToCheck = CacheHelper.get(DocubirdCache.copyJobResults);
    const jobCount: number = Array.isArray(copyJobResultsToCheck) ? copyJobResultsToCheck.length : 0;

    let completedItems = 0;
    let completedJobs = 0;
    let completedJobsResults: any[] = [];

    await Promise.all(copyJobResultsToCheck.map(async (copyJobResults, index) => {
      try {
        const destinationDirInOneDrive = moveTo.destinationDirectory.type == ComponentTypes.OneDrive || moveTo.destinationDirectory.type == ComponentTypes.OneDriveSubFolder
        const progressResults = await getCopyJobProgress({
          siteUrl: moveTo.siteUrl,
          accessToken: destinationDirInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          copyJobInfo: copyJobResults
        }).unwrap();

        if (progressResults.JobState === 0) {
          const jobItemCount = copyJobResults.SourceListItemUniqueIds?.length;
          if (jobItemCount) {
            completedJobs++;
            completedItems += jobItemCount;
            completedJobsResults.push(copyJobResults);
            dispatch(incrementContentManagerMoveToCompletedItemCount(jobItemCount));
          }
        }
      } catch (error) {
        logger(error, "useMenuItem_SelectedMoveFileCount.tsx (await getCopyJobProgress)");
      }
    }));

    if (completedJobsResults?.length > 0) {
      // Remove completed jobs from the cache
      const allCopyJobResults = CacheHelper.get(DocubirdCache.copyJobResults);
      const remainingCopyJobResults = allCopyJobResults.filter(job =>
        !completedJobsResults.some(completedJob => completedJob.JobId === job.JobId)
      );

      // Update the cache with the remaining job results
      CacheHelper.set(DocubirdCache.copyJobResults, remainingCopyJobResults);

      const moveCompleteItemIds = completedJobsResults.reduce((acc, job) => acc.concat(job.SourceListItemUniqueIds), []);

      dispatch(setContentManagerMoveToSelectedFilesIsBusyProgress(moveCompleteItemIds, false));
    }

    if (completedJobs < jobCount) {
      setFetchProgress(prev => prev + 1);
    } else {
      dispatch(setContentManagerMoveTo({ isMoveComplete: true, busyMovingFiles: false, copyJobResults: [], copyJobResultsCompletedIds: [] }));
    }
  };

  React.useEffect(() => {
    if (moveToError) {
      setMenuItem_SelectedMoveFileCount(
        {
          key: "errorMove",
          text: languageStrings.ErrorMessageGeneric,
          iconProps: { iconName: "ErrorBadge", style: { color: "red" } },
          onClick: () => {
            setIsCMMoveFilesListPanelOpen(true);
          }
        }
      );
    }
    else if (moveToProgressCountTotal > 0 && !moveTobusyMovingFiles && moveToEnabled) {
      let displayText = "";

      if (moveToMode === ContentManagerCopyMoveModeEnum.Move) {
        displayText = `${moveToProgressCountTotal} ${moveToProgressCountTotal === 1 ? languageStrings.ItemToMove.toLowerCase() : languageStrings.ItemsToMove.toLowerCase()}`;
      } 
      else if (moveToMode === ContentManagerCopyMoveModeEnum.Copy) {
        displayText = `${moveToProgressCountTotal} ${moveToProgressCountTotal === 1 ? languageStrings.ItemToCopy.toLowerCase() : languageStrings.ItemsToCopy.toLowerCase()}`;
      } 
      else if (moveToMode === ContentManagerCopyMoveModeEnum.CopyAll || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll) {
        displayText = `${languageStrings.All} ${languageStrings.ItemsSelected}`
      }

      setMenuItem_SelectedMoveFileCount(
        {
          key: "selectedMoveFileCount",
          text: displayText,
          disabled: (moveToMode === ContentManagerCopyMoveModeEnum.CopyAll || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll) && !moveToIsMoveInProgress,
          iconProps: { iconName: "Info" },
          onClick: () => {
            setIsCMMoveFilesListPanelOpen(true);
          }
        }
      );
    } else if (moveTobusyMovingFiles) {
      setMenuItem_SelectedMoveFileCount(
        {
          key: "selectedMoveFileCount",
          text: (moveToMode === ContentManagerCopyMoveModeEnum.Move || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll) ? languageStrings.MovingItems : languageStrings.CopyingItems,
          iconProps: { iconName: "Info" },
          onRenderIcon: () => {
            return <Spinner size={SpinnerSize.small} style={{ paddingRight: 5 }} />
          },
          onClick: () => {
            setIsCMMoveFilesListPanelOpen(true);
          }
        }
      );
    }
    else {
      setMenuItem_SelectedMoveFileCount(null);
    }
  }, [moveToEnabled, moveTobusyMovingFiles, moveToMode, moveToProgressCountTotal, moveToIsMoveInProgress, moveToError]);

  return {
    menuItem_SelectedMoveFileCount
  }
}